import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  isLocalMode, enableLocalMode, disableLocalMode,
  initLocalUser, clearLocal, exportLocalData, hasLocalData,
} from "@/lib/localStore";
import axios from "axios";

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const rawServerApi = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionEnabled, setSubscriptionEnabled] = useState(false);
  const [localMode, setLocalMode] = useState(isLocalMode());
  const [ledgers, setLedgers] = useState([]);
  const [activeLedger, setActiveLedger] = useState(null);

  const refetchLedgers = useCallback(async () => {
    if (isLocalMode()) {
      setLedgers([]);
      setActiveLedger(null);
      return;
    }
    try {
      const { data } = await api.get("/ledgers");
      setLedgers(data.ledgers || []);
      const active = (data.ledgers || []).find((l) => l.id === data.active_ledger_id) || (data.ledgers || [])[0] || null;
      setActiveLedger(active);
    } catch { /* not signed in */ }
  }, []);

  const fetchMe = useCallback(async () => {
    setLoading(true);
    if (isLocalMode()) {
      setLocalMode(true);
      setUser(initLocalUser());
      setLedgers([]);
      setActiveLedger(null);
      setLoading(false);
      return;
    }
    setLocalMode(false);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refetchLedgers();
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [refetchLedgers]);

  useEffect(() => {
    fetchMe();
    api.get("/features").then(({ data }) => setSubscriptionEnabled(!!data.subscription_enabled)).catch(() => {});
  }, [fetchMe]);

  const switchLedger = async (ledger_id, ledgerObj = null) => {
    try {
      await rawServerApi.post("/ledgers/switch", { ledger_id });
      // Prefer the provided object; else search current list; else refetch will fill it in
      const target = ledgerObj || ledgers.find((l) => l.id === ledger_id) || null;
      if (target) setActiveLedger(target);
      setUser((u) => (u ? { ...u, active_ledger_id: ledger_id } : u));
      if (!target) {
        // As a safety net, refetch so activeLedger becomes non-null on the next render
        await refetchLedgers();
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const continueAsGuest = () => {
    enableLocalMode();
    setLocalMode(true);
    setUser(initLocalUser());
    setLedgers([]);
    setActiveLedger(null);
    setLoading(false);
  };

  const migrateLocalToAccount = async () => {
    const payload = exportLocalData();
    if (!payload?.transactions?.length && !payload?.recurring?.length && !payload?.category_budgets?.length && !payload?.budget) {
      return { migrated: false };
    }
    try {
      const { data } = await rawServerApi.post("/import/bulk", payload);
      return { migrated: true, ...data };
    } catch (e) {
      return { migrated: false, error: e.message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data } = await rawServerApi.post("/auth/login", { email, password });
      let migration = { migrated: false };
      if (hasLocalData()) migration = await migrateLocalToAccount();
      disableLocalMode();
      clearLocal();
      setLocalMode(false);
      setUser(data);
      await refetchLedgers();
      return { ok: true, migration };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const { data } = await rawServerApi.post("/auth/register", { name, email, password });
      let migration = { migrated: false };
      if (hasLocalData()) migration = await migrateLocalToAccount();
      disableLocalMode();
      clearLocal();
      setLocalMode(false);
      setUser(data);
      await refetchLedgers();
      return { ok: true, migration };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    if (isLocalMode()) {
      setUser(false);
      return;
    }
    try { await rawServerApi.post("/auth/logout"); } catch (_) {}
    setUser(false);
    setLedgers([]);
    setActiveLedger(null);
  };

  const clearLocalData = () => {
    clearLocal();
    disableLocalMode();
    setLocalMode(false);
    setUser(false);
  };

  const updateProfile = async (updates) => {
    try {
      const { data } = await api.patch("/auth/me", updates);
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const isPro = user && user.plan === "pro" && ["active", "trialing"].includes(user.subscription_status);

  return (
    <AuthContext.Provider value={{
      user, loading, subscriptionEnabled, localMode, isPro,
      ledgers, activeLedger, refetchLedgers, switchLedger,
      login, register, logout, updateProfile, refetch: fetchMe,
      continueAsGuest, clearLocalData,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
